import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Eye, Save, Loader2, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

export function CertificateTemplateDialog({ measureId, module, onClose }) {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    if (!module) return;
    setT(null);
    api.get("/certificate-templates", { params: { measure_id: measureId, module } }).then((r) => setT(r.data)).catch(err);
  }, [measureId, module]);
  const body = () => ({ measure_id: measureId, module, course: t.course, titles: [0, 1].map((i) => t.titles[i] || "").filter(Boolean), sections: t.sections });
  const reset = async () => {
    if (!window.confirm("Eigene Inhalte löschen und Standard wiederherstellen?")) return;
    try {
      await api.delete("/certificate-templates", { params: { measure_id: measureId, module } });
      const r = await api.get("/certificate-templates", { params: { measure_id: measureId, module } });
      setT(r.data); toast.success("Standard wiederhergestellt");
    } catch (e) { err(e); }
  };
  const setSec = (i, patch) => setT({ ...t, sections: t.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const move = (i, d) => { const s = [...t.sections]; [s[i], s[i + d]] = [s[i + d], s[i]]; setT({ ...t, sections: s }); };
  const preview = async () => {
    setBusy("preview");
    const win = window.open("", "_blank");
    try {
      const r = await api.post("/certificate-templates/preview", body(), { responseType: "blob" });
      win.location.href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    } catch (e) { win?.close(); err(e); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try { await api.put("/certificate-templates", body()); toast.success("Zertifikatsinhalte gespeichert"); onClose(); } catch (e) { err(e); } finally { setBusy(""); }
  };
  return (
    <Dialog open={!!module} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cert-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">Zertifikat · {module}</DialogTitle>
          <DialogDescription className="text-xs">Name, Zeitraum und UE werden automatisch aus dem Vertrag eingesetzt.</DialogDescription>
        </DialogHeader>
        {!t ? <Loader2 className="animate-spin text-slate-300 mx-auto" /> : (
          <div className="space-y-4">
            <label className="block text-xs text-slate-600">Kurstitel
              <input value={t.course} onChange={(e) => setT({ ...t, course: e.target.value })} className="input-tech mt-1" data-testid="cert-tpl-course" />
            </label>
            <div className="space-y-1.5">
              <div className="text-xs text-slate-600">Titelzeilen (Großbuchstaben empfohlen)</div>
              {[0, 1].map((i) => (
                <input key={i} value={t.titles[i] || ""} onChange={(e) => { const a = [...t.titles]; a[i] = e.target.value; setT({ ...t, titles: a }); }}
                  placeholder={i ? "Optionale 2. Zeile" : "z. B. MODUL 1: ORIENTIERUNG & PROFILING"} className="input-tech" data-testid={`cert-tpl-title-${i}`} />
              ))}
            </div>
            <div className="space-y-2">
              <div className="text-xs text-slate-600">Inhalte (Abschnitte)</div>
              {t.sections.map((s, i) => (
                <div key={i} className="border border-slate-200 p-2 space-y-1.5" data-testid={`cert-tpl-section-${i}`}>
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-slate-400 w-4">{i + 1}.</span>
                    <input value={s.title} onChange={(e) => setSec(i, { title: e.target.value })} placeholder="Überschrift" className="input-tech flex-1" data-testid={`cert-tpl-section-title-${i}`} />
                    <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="text-slate-400 hover:text-slate-900 disabled:opacity-30"><ArrowUp size={13} /></button>
                    <button type="button" disabled={i === t.sections.length - 1} onClick={() => move(i, 1)} className="text-slate-400 hover:text-slate-900 disabled:opacity-30"><ArrowDown size={13} /></button>
                    <button type="button" onClick={() => setT({ ...t, sections: t.sections.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600" data-testid={`cert-tpl-section-del-${i}`}><Trash2 size={13} /></button>
                  </div>
                  <textarea value={s.items} onChange={(e) => setSec(i, { items: e.target.value })} rows={2} placeholder="Inhalte, getrennt mit | z. B. Analyse der Ausgangslage | Ressourcenanalyse" className="w-full border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:border-slate-900" data-testid={`cert-tpl-section-items-${i}`} />
                </div>
              ))}
              {t.sections.length < 8 && (
                <button type="button" onClick={() => setT({ ...t, sections: [...t.sections, { title: "", items: "" }] })} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="cert-tpl-section-add"><Plus size={12} /> Abschnitt hinzufügen</button>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={save} disabled={!!busy} className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-sm hover:bg-black disabled:opacity-50" data-testid="cert-tpl-save">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern</button>
              <button onClick={preview} disabled={!!busy} className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50" data-testid="cert-tpl-preview">{busy === "preview" ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Vorschau</button>
              {t.saved && <button onClick={reset} className="ml-auto inline-flex items-center gap-2 text-xs text-slate-500 hover:text-red-600" data-testid="cert-tpl-reset"><RotateCcw size={12} /> Auf Standard zurücksetzen</button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
